import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { HashLoader } from 'react-spinners';
import toast from 'react-hot-toast';
import { patientAppUrl } from '../../Utils/siteUrls';

const ParentConsent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [consentChecked, setConsentChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [error, setError] = useState('');
  const [registrationData, setRegistrationData] = useState(null);
  const [alreadyProcessedStatus, setAlreadyProcessedStatus] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);

  // Fetch registration data on mount
  useEffect(() => {
    if (!token) {
      setError('Invalid consent link. Please check your email for the correct link.');
      setIsLoading(false);
      return;
    }

    const fetchRegistrationData = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/parent-consent/`,
          {
            params: { token },
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.error) {
          if (response.data.alreadyProcessed && response.data.status) {
            setAlreadyProcessedStatus(response.data.status);
            setError('');
          } else {
            setError(response.data.error);
            toast.error(response.data.error);
          }
        } else {
          setRegistrationData(response.data);
        }
      } catch (err) {
        console.error('Error fetching registration data:', err);
        const data = err.response?.data;
        if (data?.alreadyProcessed && data?.status) {
          setAlreadyProcessedStatus(data.status);
          setError('');
        } else {
          const errorMsg = data?.error || 'Failed to load registration details. Please check your email link.';
          setError(errorMsg);
          toast.error(errorMsg);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchRegistrationData();
  }, [token]);

  const handleApprove = async () => {
    if (!consentChecked) {
      setError('Please check the consent checkbox to proceed.');
      toast.error('Please check the consent checkbox to proceed.');
      return;
    }

    setIsApproving(true);
    setError('');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/parent-consent/approve/`,
        {
          token: token,
          approved: true
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.error) {
        setError(response.data.error);
        toast.error(response.data.error);
      } else {
        // Show success modal
        setShowSuccessModal(true);
      }
    } catch (err) {
      console.error('Error approving consent:', err);
      const errorMsg = err.response?.data?.error || 'Failed to approve consent. Please try again.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    if (!window.confirm('Are you sure you want to reject this registration? This action cannot be undone.')) {
      return;
    }

    setIsRejecting(true);
    setError('');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/parent-consent/approve/`,
        {
          token: token,
          approved: false
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.error) {
        setError(response.data.error);
        toast.error(response.data.error);
      } else {
        // Show decline confirmation
        setShowDeclineModal(true);
      }
    } catch (err) {
      console.error('Error rejecting consent:', err);
      const errorMsg = err.response?.data?.error || 'Failed to reject registration. Please try again.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsRejecting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#111920] flex flex-col items-center justify-center">
        <HashLoader size={50} color="#0CC8E8" loading={true} />
        <p className="mt-4 text-gray-700 dark:text-gray-300">Loading registration details...</p>
      </div>
    );
  }

  // Already processed (e.g. parent already approved or rejected) — show info message
  if (alreadyProcessedStatus && !registrationData) {
    const infoMessages = {
      PENDING_CHILD_EMAIL_VERIFICATION: "You've already approved this registration. The child has been sent an email to verify their address. No further action is needed from you.",
      EMAIL_VERIFIED: "This registration has already been completed. The child's email has been verified. No further action is needed from you.",
      PENDING_PAYMENT_METHOD: "This registration has already been approved. A payment method is being set up. No further action is needed from you.",
      ACTIVE: "This child account is already active. No further action is needed from you.",
      REJECTED: "This registration was declined. No further action is needed.",
      EXPIRED: "This consent link has expired. No further action is needed."
    };
    const infoTitle = alreadyProcessedStatus === 'REJECTED' ? 'Registration declined' : 'Already processed';
    const message = infoMessages[alreadyProcessedStatus] || "This registration has already been processed. No further action is needed from you.";

    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#111920] flex flex-col items-center justify-center px-4">
        <div className="max-w-md w-full bg-white dark:bg-[#1B2731] rounded-xl shadow-lg p-6 border border-transparent dark:border-[#283642]">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{infoTitle}</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">{message}</p>
            <button
              onClick={() => navigate('/')}
              className="bg-gray-900 dark:bg-[#0CC8E8] hover:bg-gray-800 dark:hover:bg-[#2DEEAA] text-white rounded-full px-6 py-2 font-semibold transition-all duration-300"
            >
              Return to home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error && !registrationData) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#111920] flex flex-col items-center justify-center px-4">
        <div className="max-w-md w-full bg-white dark:bg-[#1B2731] rounded-xl shadow-lg p-6 border border-transparent dark:border-[#283642]">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Error</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="bg-gray-900 dark:bg-[#0CC8E8] hover:bg-gray-800 dark:hover:bg-[#2DEEAA] text-white rounded-full px-6 py-2 font-semibold transition-all duration-300"
            >
              Go to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#111920] flex flex-col py-8 px-4">
      <div className="max-w-4xl w-full mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Parental Consent
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            A child account registration has been initiated, and we require your consent to proceed.
          </p>
        </div>

        {registrationData && (
          <div className="bg-white dark:bg-[#1B2731] rounded-xl shadow-lg border border-transparent dark:border-[#283642] p-6 mb-6">
            {/* Child Information Section */}
            <div className="mb-6">
              <h3 className="font-bold text-gray-900 dark:text-white text-lg mb-4">Child Account Information</h3>
              <div className="bg-gray-50 dark:bg-[#24303A] rounded-lg p-4 space-y-2">
                <div>
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Child's Name:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{registrationData.childData?.fullName || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Child's Email:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{registrationData.childData?.email || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Date of Birth:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{registrationData.childData?.dob || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Sex Assigned at Birth:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{registrationData.childData?.sexAssignedAtBirth || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Gender Identity:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{registrationData.childData?.genderIdentity || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Verification trigger time:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">
                    {registrationData.consentRequestSentAt
                      ? new Date(registrationData.consentRequestSentAt).toLocaleString(undefined, {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                          hour12: true
                        })
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Parent Information Section */}
            <div className="mb-6">
              <h3 className="font-bold text-gray-900 dark:text-white text-lg mb-4">Your Information</h3>
              <div className="bg-gray-50 dark:bg-[#24303A] rounded-lg p-4 space-y-2">
                <div>
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Your Name:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">
                    {registrationData.parentData?.firstName} {registrationData.parentData?.lastName}
                  </span>
                </div>
                <div>
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Your Email:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{registrationData.parentData?.email || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Relationship:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{registrationData.parentData?.relationship || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Consent Content */}
            <div className="mb-6 max-h-96 overflow-y-auto pr-2 space-y-3 text-gray-700 dark:text-gray-300 text-sm">
              <h2 className="font-bold text-gray-900 dark:text-white text-lg">Consent</h2>
              <p>By confirming your consent, you expressly agree to the following:</p>

              <ul className="list-disc pl-5 space-y-2">
                <li>I am the parent, legal guardian, or caregiver of the child listed above.</li>
                <li>I consent to my child using IzzyAI and its features under my supervision.</li>
                <li>I agree to IzzyAI's <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-cyan-400 hover:underline">Terms of Use</a>, <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-cyan-400 hover:underline">Privacy Policy</a>, and <a href="/service" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-cyan-400 hover:underline">Service Agreement</a>.</li>
                <li>I understand that IzzyAI is a supplementary tool and does not replace professional medical advice or therapy.</li>
                <li>I acknowledge that I may review or withdraw my consent by contacting IzzyAI at <a href="mailto:hello@izzyai.com" className="text-blue-600 dark:text-cyan-400 hover:underline">hello@izzyai.com</a>.</li>
                <li>I understand that a payment method will be required to activate the account (no charge during beta period).</li>
              </ul>

              <div 
                className="mt-4 pt-4 flex items-start gap-3 p-4 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-[#1a2329] hover:border-blue-500 dark:hover:border-cyan-400 transition-colors cursor-pointer"
                onClick={() => setConsentChecked(!consentChecked)}
              >
                <div className="flex-shrink-0 mt-0.5">
                  <div className={`
                    w-6 h-6 rounded border-2 flex items-center justify-center transition-all
                    ${consentChecked 
                      ? 'bg-blue-600 dark:bg-cyan-400 border-blue-600 dark:border-cyan-400' 
                      : 'bg-white dark:bg-[#24303A] border-gray-400 dark:border-gray-500'
                    }
                  `}>
                    {consentChecked && (
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>
                <label 
                  htmlFor="consentCheckbox" 
                  className="flex-1 text-gray-700 dark:text-gray-300 cursor-pointer font-medium"
                >
                  I agree to the above terms and provide my consent.
                </label>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={handleApprove}
                disabled={!consentChecked || isApproving || isRejecting}
                className="bg-gray-900 dark:bg-[#0CC8E8] hover:bg-gray-800 dark:hover:bg-[#2DEEAA] text-white rounded-full px-8 py-3 font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 min-w-[200px]"
              >
                {isApproving ? (
                  <>
                    <HashLoader size={20} color="#fff" />
                    <span>Processing...</span>
                  </>
                ) : (
                  'Approve & Create Account'
                )}
              </button>
              <button
                onClick={handleReject}
                disabled={isApproving || isRejecting}
                className="bg-red-600 dark:bg-red-500 hover:bg-red-700 dark:hover:bg-red-600 text-white rounded-full px-8 py-3 font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 min-w-[200px]"
              >
                {isRejecting ? (
                  <>
                    <HashLoader size={20} color="#fff" />
                    <span>Processing...</span>
                  </>
                ) : (
                  'Reject Registration'
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Success Modal - Account Created */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1B2731] rounded-xl shadow-2xl max-w-md w-full p-6 border border-transparent dark:border-[#283642]">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Account Created Successfully!</h2>
              <div className="text-left text-gray-700 dark:text-gray-300 space-y-3 mb-6">
                <p>Your child's account has been created successfully.</p>
                <p><strong>Next Steps:</strong></p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>An email verification has been sent to your child's email address</li>
                  <li>Your child needs to verify their email to continue</li>
                  <li>You will need to add a payment method (no charge during beta period)</li>
                </ul>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
                  You will be redirected to the app to complete the setup.
                </p>
              </div>
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  // Navigate to app.izzyai.com
                  window.location.href = patientAppUrl();
                }}
                className="w-full bg-gray-900 dark:bg-[#0CC8E8] hover:bg-gray-800 dark:hover:bg-[#2DEEAA] text-white rounded-full px-6 py-3 font-semibold transition-all duration-300"
              >
                Continue to App
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Decline Modal */}
      {showDeclineModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1B2731] rounded-xl shadow-2xl max-w-md w-full p-6 border border-transparent dark:border-[#283642]">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Registration Declined</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-6">
                The child account registration has been declined. A notification email has been sent to the child's email address.
              </p>
              <button
                onClick={() => {
                  setShowDeclineModal(false);
                  window.location.href = patientAppUrl();
                }}
                className="w-full bg-gray-900 dark:bg-[#0CC8E8] hover:bg-gray-800 dark:hover:bg-[#2DEEAA] text-white rounded-full px-6 py-3 font-semibold transition-all duration-300"
              >
                Return to App
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentConsent;
